import React from 'react';
import { Box, Button, Text, Group } from '@mantine/core';
import { IconArrowUpRight, IconCalendarPlus } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from '@inertiajs/react';

interface ManageBadgeProps {
  entityType: 'artist' | 'organizer' | 'post' | 'event' | 'lesson';
  entitySlug: string;
  userHasAccess: boolean;
  customText?: string;
  bookingsUrl?: string;
  editUrl?: string;
}

const ManageBadge: React.FC<ManageBadgeProps> = ({
  entityType,
  entitySlug,
  userHasAccess,
  customText,
  bookingsUrl,
  editUrl,
}) => {
  // Use media query to detect mobile screens
  const isMobile = useMediaQuery('(max-width: 600px)');

  if (!userHasAccess) {
    return null;
  }

  // Map entity types to their respective URLs
  const urlMap = {
    artist: `/artists/${entitySlug}/edit`,
    organizer: `/o/${entitySlug}/edit`,
    post: `/posts/${entitySlug}/edit`,
    event: `/events/${entitySlug}/edit`,
    lesson: `/lessons/${entitySlug}/edit`, // fallback if editUrl not provided
  };

  // Default text based on entity type
  const defaultTextMap = {
    artist: 'You have manage access for this artist.',
    organizer: 'You have manage access for this organizer.',
    post: 'You have manage access for this post.',
    event: 'You have manage access for this event.',
    lesson: 'You have manage access for this private lesson.',
  };

  // Use the provided editUrl if available, otherwise use the mapped URL
  const manageLinkUrl = editUrl || urlMap[entityType];
  const displayText = customText || defaultTextMap[entityType];
  const isLesson = entityType === 'lesson';

  // URL for private lesson management with bookings tab active
  const privateLessonBookingsUrl =
    bookingsUrl || `/users/${entitySlug}/user-settings?section=privateLessons&tab=pl-bookings`;

  return (
    <Box
      mb="lg"
      w="100%"
      style={{
        borderRadius: '12px',
        backgroundColor: '#EA580C10',
        border: '1px solid #EA580C',
        padding: '8px 16px',

        /* use grid: text takes all available space, button auto-sizes */
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <Text
        fw={500}
        style={{
          color: '#EA580C',
          fontSize: isMobile ? '12px' : '0.8rem',
          /* allow wrapping onto multiple lines */
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
        }}
      >
        {displayText}
      </Text>

      <Group gap="xs" style={{ flexShrink: 0 }}>
        {isLesson && (
          <Link
            href={privateLessonBookingsUrl}
            as="a"
            /* keep link/button together */
            style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
            className={`mantine-button-class ${isMobile ? 'mobile' : ''}`}
            onClick={(e) => {
              if (typeof window !== 'undefined' && window.location.pathname.includes('/user-settings')) {
                e.preventDefault();
                localStorage.setItem('activeSettingsSection', 'privateLessons');
                localStorage.setItem('activePrivateLessonsTab', 'pl-bookings');
                window.dispatchEvent(
                  new CustomEvent('navigateToPrivateLessonBookings', {
                    detail: { section: 'privateLessons', tab: 'pl-bookings' },
                  }),
                );
              }
            }}
          >
            Manage Bookings <IconCalendarPlus size={16} style={{ marginLeft: '4px' }} />
          </Link>
        )}
        <Link href={manageLinkUrl} style={{ textDecoration: 'none' }}>
          <Button
            variant="filled"
            color="#EA580C"
            rightSection={<IconArrowUpRight size={12} />}
            style={{ whiteSpace: 'nowrap' }}
            size={isMobile ? 'xs' : 'sm'}
          >
            Manage
          </Button>
        </Link>
      </Group>
    </Box>
  );
};

export default ManageBadge;
